import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import './registerPage.css';
import { loadAdsTag, readAdVisit, trackRegistrationConversion } from '../../../utils/adSource';

/**
 * Self-serve registration (PLAN_registration.md Phase 1, 2026-08-04).
 *
 *   Step 1 — details + mandatory read-and-agree T&C
 *   Step 2 — the WhatsApp code; verifying CREATES the account and signs the
 *            session cookie, so everything after runs authenticated
 *   Step 3 — billing details (everyone) + trade license (business) + TRN and
 *            VAT certificate (VAT-registered), through the same client
 *            endpoints the portal uses. If they bail mid-step-3, the
 *            RequiredDocsGate catches the missing documents at next login —
 *            this page is the fast path, the gate is the backstop.
 */
const TERMS_URL = 'https://holidayhomecleaning.ae/terms-and-conditions';

// The seven emirates — the only valid billing "city/state" in the UAE.
const EMIRATES = ['Abu Dhabi', 'Dubai', 'Sharjah', 'Ajman', 'Umm Al Quwain', 'Ras Al Khaimah', 'Fujairah']
  .map((e) => ({ value: e, label: e }));

const emirateSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 46,
    borderRadius: 11,
    background: state.isFocused ? '#fff' : '#fcfcfd',
    borderColor: state.isFocused ? '#ff5a60' : '#e4e7ec',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(255, 90, 96, 0.14)' : 'none',
    '&:hover': { borderColor: state.isFocused ? '#ff5a60' : '#e4e7ec' },
    fontSize: 14.5,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
};

async function uploadDoc(file, type) {
  const presign = await axios.post(
    '/api/client-requests/documents/upload-url',
    { fileName: file.name, fileType: file.type },
    { withCredentials: true }
  );
  await fetch(presign.data.url, {
    method: 'PUT',
    headers: { 'Content-Type': file.type, 'x-amz-acl': 'public-read' },
    body: file,
  });
  await axios.post(
    '/api/client-requests/documents',
    { file_url: presign.data.fileUrl, file_key: presign.data.fileKey, type },
    { withCredentials: true }
  );
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [existsHint, setExistsHint] = useState(false);
  const [registrationId, setRegistrationId] = useState(null);
  const [resent, setResent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const codeRef = useRef(null);

  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    client_type: 'individual', company_name: '',
    password: '', accepted_terms: false,
  });
  const [billing, setBilling] = useState({
    address_line_1: '', emirate: 'Dubai', country: 'United Arab Emirates',
    is_vat_registered: false, trn_number: '',
  });
  const [licenseFile, setLicenseFile] = useState(null);
  const [vatFile, setVatFile] = useState(null);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const setBill = (k) => (e) =>
    setBilling((b) => ({ ...b, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const isBusiness = form.client_type === 'business';
  // `ad`: the Google Ads ref this device arrived with, if any — the lead is
  // then sourced "App - Google Ads" (utils/adSource.js).
  const startPayload = () => ({ ...form, phone: form.phone.trim(), ad: readAdVisit() });

  useEffect(() => { loadAdsTag(); }, []);

  const handleStart = async (e) => {
    e.preventDefault();
    setBusy(true); setError(''); setExistsHint(false);
    try {
      const res = await axios.post('/api/register/start', startPayload(), { withCredentials: true });
      setRegistrationId(res.data.registration_id);
      setStep(2);
      setTimeout(() => codeRef.current?.focus(), 60);
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed — please try again.');
      if (err.response?.data?.reason === 'exists') setExistsHint(true);
    } finally {
      setBusy(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const code = codeRef.current?.value?.trim();
    setBusy(true); setError('');
    try {
      await axios.post('/api/register/verify',
        { registration_id: registrationId, code }, { withCredentials: true });
      localStorage.setItem('userRole', 'client');
      localStorage.setItem('firstName', form.first_name);
      trackRegistrationConversion();
      setStep(3); // authenticated from here on
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Verification failed — please try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    setBusy(true); setError(''); setResent(false);
    try {
      const res = await axios.post('/api/register/resend', startPayload(), { withCredentials: true });
      setRegistrationId(res.data.registration_id);
      setResent(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not resend the code.');
    } finally {
      setBusy(false);
    }
  };

  const handleFinish = async (e) => {
    e.preventDefault();
    if (isBusiness && !licenseFile) { setError('Please upload your trade license.'); return; }
    if (billing.is_vat_registered && !vatFile) { setError('Please upload your VAT certificate.'); return; }
    setBusy(true); setError('');
    try {
      await axios.put('/api/client-requests/settings/billing', billing, { withCredentials: true });
      if (isBusiness && licenseFile) await uploadDoc(licenseFile, 'trade_license');
      if (billing.is_vat_registered && vatFile) await uploadDoc(vatFile, 'vat_certificate');
      navigate('/client/home', { replace: true });
      window.location.reload(); // App.js reads the role once — same as login
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save — please try again.');
      setBusy(false);
    }
  };

  const stepTitle = { 1: 'Create your account', 2: 'Check WhatsApp', 3: 'Billing & documents' }[step];
  const stepSub = {
    1: 'Book cleanings for your holiday homes in minutes — no calls, no waiting.',
    2: `We sent a 6-digit code to ${form.phone}. Enter it below to continue.`,
    3: isBusiness
      ? 'Last step — your billing details and business documents.'
      : 'Last step — your billing details.',
  }[step];

  const fileLabel = (f) => (f ? f.name : 'Choose a PDF or photo…');

  return (
    <div className="regp">
      <div className="regp__card">
        {step === 1 && <Link to="/login" className="regp__back">← Back to login</Link>}

        <img src="/logo.png" alt="Holiday Home Cleaning" className="regp__logo" />

        <div className="regp__steps" aria-hidden="true">
          {[1, 2, 3].map((n) => (
            <span key={n} className={`regp__stepDot ${step >= n ? 'is-on' : ''}`}>{n}</span>
          ))}
        </div>

        <h1 className="regp__title">{stepTitle}</h1>
        <p className="regp__sub">{stepSub}</p>

        {step === 1 && (
          <form onSubmit={handleStart} className="regp__form">
            <div className="regp__row">
              <div className="regp__group">
                <label className="regp__label">First name</label>
                <input className="regp__input" value={form.first_name} onChange={set('first_name')} required autoFocus />
              </div>
              <div className="regp__group">
                <label className="regp__label">Last name</label>
                <input className="regp__input" value={form.last_name} onChange={set('last_name')} />
              </div>
            </div>

            <div className="regp__group">
              <label className="regp__label">Email</label>
              <input className="regp__input" type="email" value={form.email} onChange={set('email')} required />
            </div>

            <div className="regp__group">
              <label className="regp__label">WhatsApp number</label>
              {/* Same PhoneInput as "Log in with WhatsApp" — one phone field
                  everywhere (2026-08-04). */}
              <PhoneInput
                defaultCountry="ae"
                value={form.phone}
                onChange={(phone) => setForm((f) => ({ ...f, phone }))}
                placeholder="WhatsApp number"
                className="regp__phoneWrap"
                inputClassName="regp__phoneInput"
              />
              <span className="regp__hintline">We'll send a verification code to this number.</span>
            </div>

            <div className="regp__group">
              <label className="regp__label">Account type</label>
              <div className="regp__typeToggle" role="radiogroup">
                {['individual', 'business'].map((t) => (
                  <button key={t} type="button"
                    className={`regp__typeBtn ${form.client_type === t ? 'is-active' : ''}`}
                    onClick={() => setForm((f) => ({ ...f, client_type: t }))}>
                    {t === 'individual' ? 'Individual' : 'Business'}
                  </button>
                ))}
              </div>
            </div>

            {isBusiness && (
              <div className="regp__group">
                <label className="regp__label">Company name</label>
                <input className="regp__input" value={form.company_name} onChange={set('company_name')} required />
              </div>
            )}

            <div className="regp__group">
              <label className="regp__label">Password</label>
              <div className="regp__pwWrap">
                <input className="regp__input" type={showPassword ? 'text' : 'password'} minLength={8}
                  value={form.password} onChange={set('password')} required />
                {/* Same eye as the login page — one visual language. */}
                <button type="button" className="regp__pwToggle"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  title={showPassword ? 'Hide password' : 'Show password'}
                  onClick={() => setShowPassword((v) => !v)}>
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </button>
              </div>
              <span className="regp__hintline">At least 8 characters.</span>
            </div>

            <label className="regp__terms">
              <input type="checkbox" checked={form.accepted_terms} onChange={set('accepted_terms')} required />
              <span>
                I have read and agree to the{' '}
                <a href={TERMS_URL} target="_blank" rel="noreferrer">Terms &amp; Conditions</a>.
                <span className="regp__termsNote">
                  Please read them carefully — they cover how bookings, payments,
                  cancellations and access to your property work.
                </span>
              </span>
            </label>

            {error && (
              <div className="regp__error">
                {error}
                {existsHint && (
                  <>
                    {' '}
                    <Link
                      to={`/login?otp=whatsapp&phone=${encodeURIComponent(form.phone.trim())}`}
                      className="regp__errorLink"
                    >
                      Log in with WhatsApp instead
                    </Link>
                  </>
                )}
              </div>
            )}

            <button type="submit" className="regp__submit" disabled={busy}>
              {busy ? 'Sending code…' : 'Continue'}
            </button>

            <p className="regp__foot">
              Already have an account? <Link to="/login">Log in</Link> — or, if your
              company is already a client, ask our team to add you to that account
              instead of registering a new one.
            </p>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleVerify} className="regp__form">
            <div className="regp__group">
              <label className="regp__label">Verification code</label>
              <input ref={codeRef} className="regp__input regp__input--code" inputMode="numeric"
                pattern="\d{6}" maxLength={6} placeholder="••••••" required />
            </div>

            {error && <div className="regp__error">{error}</div>}
            {resent && <div className="regp__ok">A fresh code is on its way.</div>}

            <button type="submit" className="regp__submit" disabled={busy}>
              {busy ? 'Verifying…' : 'Verify number'}
            </button>

            <div className="regp__stepLinks">
              <button type="button" className="regp__linkBtn" onClick={handleResend} disabled={busy}>
                Resend code
              </button>
              <button type="button" className="regp__linkBtn" onClick={() => { setStep(1); setError(''); }}>
                Edit details
              </button>
            </div>
          </form>
        )}

        {step === 3 && (
          <form onSubmit={handleFinish} className="regp__form">
            <div className="regp__noCard">
              💳 No credit card required — these details only appear on your invoices.
            </div>
            <div className="regp__group">
              <label className="regp__label">Billing address</label>
              <input className="regp__input" placeholder="Street, building, unit"
                value={billing.address_line_1} onChange={setBill('address_line_1')} required autoFocus />
            </div>

            <div className="regp__row">
              <div className="regp__group">
                <label className="regp__label">Emirate</label>
                <Select
                  options={EMIRATES}
                  value={EMIRATES.find((e) => e.value === billing.emirate)}
                  onChange={(opt) => setBilling((b) => ({ ...b, emirate: opt.value }))}
                  styles={emirateSelectStyles}
                  menuPortalTarget={document.body}
                  isSearchable={false}
                />
              </div>
              <div className="regp__group">
                <label className="regp__label">Country</label>
                <input className="regp__input" value={billing.country} readOnly />
              </div>
            </div>

            {isBusiness && (
              <>
                <div className="regp__group">
                  <label className="regp__label">Trade license <span className="regp__req">required</span></label>
                  <label className="regp__file">
                    <input type="file" accept="application/pdf,image/*"
                      onChange={(e) => setLicenseFile(e.target.files?.[0] || null)} />
                    <span className={licenseFile ? 'is-picked' : ''}>{fileLabel(licenseFile)}</span>
                  </label>
                </div>

                <label className="regp__terms">
                  <input type="checkbox" checked={billing.is_vat_registered} onChange={setBill('is_vat_registered')} />
                  <span>We are VAT registered</span>
                </label>

                {billing.is_vat_registered && (
                  <>
                    <div className="regp__group">
                      <label className="regp__label">TRN (15 digits)</label>
                      <input className="regp__input" inputMode="numeric" maxLength={15}
                        value={billing.trn_number} onChange={setBill('trn_number')} required />
                    </div>
                    <div className="regp__group">
                      <label className="regp__label">VAT certificate <span className="regp__req">required</span></label>
                      <label className="regp__file">
                        <input type="file" accept="application/pdf,image/*"
                          onChange={(e) => setVatFile(e.target.files?.[0] || null)} />
                        <span className={vatFile ? 'is-picked' : ''}>{fileLabel(vatFile)}</span>
                      </label>
                    </div>
                  </>
                )}
              </>
            )}

            {error && <div className="regp__error">{error}</div>}

            <button type="submit" className="regp__submit" disabled={busy}>
              {busy ? 'Saving…' : 'Finish & go to my dashboard'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
